import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { generatePath, Link } from 'react-router-dom';
import { Check, LoaderCircle, Pencil, TrashIcon, XIcon } from 'lucide-react';

import { assigneeOptionValue, findTeamMemberByAssigneeId, useGetTeamMembers } from '@/api/settings/useGetTeamMembers';
import { useAddComment } from '@/api/tickets/useAddComment';
import { useGetComments } from '@/api/tickets/useGetComments';
import { useGetTicketActivity } from '@/api/tickets/useGetTicketActivity';
import type { Ticket as ApiTicket } from '@/api/tickets/useGetTickets';
import { useUpdateTicket } from '@/api/tickets/useUpdateTicket';
import { useGetAudienceByWorkspace } from '@/api/workspaces/useGetAudienceByWorkspace';
import { brandColors } from '@/app/constants';
import Avatar from '@/component-library/primitives/Avatar/Avatar';
import { Button } from '@/component-library/primitives/Button/Button';
import { Member } from '@/models/AudienceByWorkspace';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { AppRoutes } from '@/utils/routes/router';
import { getSignalIdFromTicketMetadata } from '@/utils/ticketSignalMetadata';

import {
  audienceLinkLabel,
  AudienceMemberListPicker,
  dueDateToDateInputValue,
  formatTicketActivityActor,
} from './ticketModalShared';

/** Local ticket shape aligned with Tickets board (priority/status may be numeric from API). */
export type TicketDetailTicket = ApiTicket & { ticketId: string; voteCount?: number };

export interface TicketDetailModalProps {
  open: boolean;
  ticket: TicketDetailTicket | null;
  onClose: () => void;
}

const tagColors = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-purple-100 text-purple-800 border-purple-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-teal-100 text-teal-800 border-teal-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
];

function getTagColor(tag: string) {
  const hash = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return tagColors[hash % tagColors.length];
}

function getDisplayName(member: { userName?: string; email?: string }) {
  if (member.userName && member.userName !== member.email) {
    return member.userName;
  }
  return member.email ? member.email.split('@')[0] : 'Unknown';
}

export const TicketDetailModal: React.FC<TicketDetailModalProps> = ({ open, ticket, onClose }) => {
  const { data: teamMembers = [] } = useGetTeamMembers();
  const { data: audienceWorkspace } = useGetAudienceByWorkspace({ mode: 'all' });
  const audienceMembers = useMemo(() => {
    const raw = audienceWorkspace?.members ?? audienceWorkspace?.audience ?? [];
    return Array.isArray(raw) ? (raw as Member[]) : [];
  }, [audienceWorkspace]);
  const { user } = useAuth();

  const [selectedTicket, setSelectedTicket] = useState<TicketDetailTicket | null>(null);

  const readyTicket = useMemo((): TicketDetailTicket | null => {
    if (!open || !ticket) return null;
    const tid = String(ticket.ticketId || ticket.id || '');
    if (!tid) return null;
    return { ...ticket, ticketId: tid } as TicketDetailTicket;
  }, [open, ticket]);

  useLayoutEffect(() => {
    setSelectedTicket(readyTicket);
  }, [readyTicket]);

  const { data: commentsData, isLoading: commentsLoading } = useGetComments(readyTicket?.ticketId ?? null);
  const { data: activityRows = [], isLoading: activityLoading } = useGetTicketActivity(readyTicket?.ticketId ?? null);

  const updateTicketMutation = useUpdateTicket();
  const addCommentMutation = useAddComment();
  const resetAddCommentMutationRef = useRef(addCommentMutation.reset);
  resetAddCommentMutationRef.current = addCommentMutation.reset;

  const [editingTicketTitle, setEditingTicketTitle] = useState(false);
  const [editingTicketDescription, setEditingTicketDescription] = useState(false);
  const [tempTicketTitle, setTempTicketTitle] = useState('');
  const [tempTicketDescription, setTempTicketDescription] = useState('');
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [isAddingLabel, setIsAddingLabel] = useState(false);
  const [memberPickerQuery, setMemberPickerQuery] = useState('');
  const [addAudiencePickerOpen, setAddAudiencePickerOpen] = useState(false);
  const [detailAudiencePickerOpen, setDetailAudiencePickerOpen] = useState(false);
  const addAudiencePickerRef = useRef<HTMLDivElement>(null);
  const detailAudiencePickerRef = useRef<HTMLDivElement>(null);
  const [newComment, setNewComment] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const assigneeOverlayRef = useRef<HTMLDivElement>(null);
  const [showAssigneeSelector, setShowAssigneeSelector] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');

  const filteredTeamMembers = teamMembers.filter((member) =>
    getDisplayName(member).toLowerCase().includes(assigneeSearchTerm.toLowerCase()),
  );

  const linksFromMemberIds = useCallback(
    (ids: string[]): TicketDetailTicket['audienceLinks'] => {
      if (ids.length === 0) return undefined;
      return ids.map((audienceMemberId) => {
        const m = audienceMembers.find((x) => x.id === audienceMemberId);
        return {
          audienceMemberId,
          audienceMemberEmail: m?.email ?? null,
          audienceMemberName: m?.name ?? null,
        };
      });
    },
    [audienceMembers],
  );

  const persistTicketAudienceMembers = async (t: TicketDetailTicket, nextIds: string[]) => {
    await updateTicketMutation.mutateAsync({
      ticketId: t.ticketId,
      data: { audienceMemberIds: nextIds },
    });
    setSelectedTicket({
      ...t,
      audienceLinks: linksFromMemberIds(nextIds),
    });
  };

  const availableForDetailTicket = useMemo(() => {
    const cur = new Set(((selectedTicket ?? readyTicket)?.audienceLinks ?? []).map((l) => String(l.audienceMemberId)));
    const q = memberPickerQuery.trim().toLowerCase();
    return audienceMembers.filter((m) => {
      if (cur.has(m.id)) return false;
      if (!q) return true;
      const name = (m.name || '').toLowerCase();
      const email = (m.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [audienceMembers, memberPickerQuery, selectedTicket, readyTicket]);

  const readyTicketId = readyTicket?.ticketId;

  useEffect(() => {
    if (!readyTicketId) return;
    setMemberPickerQuery('');
    setAddAudiencePickerOpen(false);
    setDetailAudiencePickerOpen(false);
    setEditingTicketTitle(false);
    setEditingTicketDescription(false);
    setNewComment('');
    resetAddCommentMutationRef.current();
  }, [readyTicketId]);

  const openAssigneeSelector = useCallback(() => {
    setDetailAudiencePickerOpen(false);
    setAddAudiencePickerOpen(false);
    setMemberPickerQuery('');
    setShowAssigneeSelector(true);
  }, []);

  useEffect(() => {
    if (!addAudiencePickerOpen && !detailAudiencePickerOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (addAudiencePickerOpen && addAudiencePickerRef.current && !addAudiencePickerRef.current.contains(t)) {
        setAddAudiencePickerOpen(false);
      }
      if (detailAudiencePickerOpen && detailAudiencePickerRef.current && !detailAudiencePickerRef.current.contains(t)) {
        setDetailAudiencePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addAudiencePickerOpen, detailAudiencePickerOpen]);

  const handleClose = useCallback(() => {
    onClose();
    setMemberPickerQuery('');
    setAddAudiencePickerOpen(false);
    setDetailAudiencePickerOpen(false);
    setShowAssigneeSelector(false);
    setAssigneeSearchTerm('');
    setEditingTicketTitle(false);
    setEditingTicketDescription(false);
    setNewComment('');
    resetAddCommentMutationRef.current();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!modalRef.current) return;
      const t = event.target as Node;
      if (modalRef.current.contains(t)) return;
      if (assigneeOverlayRef.current?.contains(t)) return;
      handleClose();
    };

    // Defer so the same click that opened the modal does not immediately count as "outside" and close it
    // (or churn React Query / state in a tight loop).
    const scheduleId = window.setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      window.clearTimeout(scheduleId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, handleClose]);

  const handleSaveTicketTitle = async () => {
    if (!selectedTicket || !tempTicketTitle.trim()) return;
    try {
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { title: tempTicketTitle },
      });
      setSelectedTicket({ ...selectedTicket, title: tempTicketTitle });
      setEditingTicketTitle(false);
    } catch (error) {
      console.error('Failed to update ticket title:', error);
    }
  };

  const handleSaveTicketDescription = async () => {
    if (!selectedTicket) return;
    try {
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { description: tempTicketDescription },
      });
      setSelectedTicket({ ...selectedTicket, description: tempTicketDescription });
      setEditingTicketDescription(false);
    } catch (error) {
      console.error('Failed to update ticket description:', error);
    }
  };

  const updateTicketAssignee = async (ticketId: string, assigneeId: string | undefined | null) => {
    try {
      const newAssignee = assigneeId ? findTeamMemberByAssigneeId(teamMembers, assigneeId) : null;
      if (selectedTicket && selectedTicket.ticketId === ticketId) {
        setSelectedTicket({
          ...selectedTicket,
          assigneeId: assigneeId || undefined,
          assigneeName: newAssignee ? getDisplayName(newAssignee) : undefined,
          assigneeEmail: newAssignee?.email || undefined,
        });
      }
      await updateTicketMutation.mutateAsync({
        ticketId,
        data: {
          updateAssignee: true,
          assigneeId: assigneeId || null,
          assigneeName: newAssignee ? getDisplayName(newAssignee) : null,
          assigneeEmail: newAssignee?.email || null,
        },
      });
      const commentText = assigneeId
        ? `Assigned to ${newAssignee ? getDisplayName(newAssignee) : 'Unknown User'}`
        : 'Removed assignee';
      addCommentMutation
        .mutateAsync({ ticketId, data: { content: commentText, isInternal: false } })
        .catch((err) => console.warn('Failed to add assignee change comment (non-critical):', err));
    } catch (error) {
      console.error('Failed to update ticket assignee:', error);
      if (selectedTicket && selectedTicket.ticketId === ticketId) {
        setSelectedTicket({ ...selectedTicket });
      }
    }
  };

  const handleAddComment = async () => {
    if (addCommentMutation.isLoading || !newComment.trim() || !selectedTicket) return;
    try {
      await addCommentMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { content: newComment.trim(), isInternal: false },
      });
      setNewComment('');
      addCommentMutation.reset();
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleAddLabel = async () => {
    if (!newLabel.trim() || !selectedTicket || isAddingLabel) return;
    const tagToAdd = newLabel.trim();
    const previousTags = selectedTicket.tags || [];
    const updatedTags = [...previousTags, tagToAdd];
    try {
      setIsAddingLabel(true);
      setSelectedTicket({ ...selectedTicket, tags: updatedTags });
      setNewLabel('');
      setShowLabelInput(false);
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { tags: updatedTags },
      });
    } catch (error) {
      console.error('Failed to add label:', error);
      setSelectedTicket({ ...selectedTicket, tags: previousTags });
      setNewLabel(tagToAdd);
      setShowLabelInput(true);
    } finally {
      setIsAddingLabel(false);
    }
  };

  const handleRemoveLabel = async (labelToRemove: string) => {
    if (!selectedTicket) return;
    try {
      const updatedTags = (selectedTicket.tags || []).filter((tag) => tag !== labelToRemove);
      setSelectedTicket({ ...selectedTicket, tags: updatedTags });
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { tags: updatedTags },
      });
    } catch (error) {
      console.error('Failed to remove label:', error);
      setSelectedTicket({ ...selectedTicket, tags: selectedTicket.tags || [] });
    }
  };

  const handleArchiveTicket = async () => {
    if (!selectedTicket) return;
    try {
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { archived: true },
      });
      setSelectedTicket({ ...selectedTicket, archived: true });
    } catch (e) {
      console.error('Failed to archive ticket:', e);
    }
  };

  const handleRestoreTicket = async () => {
    if (!selectedTicket) return;
    try {
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { archived: false },
      });
      setSelectedTicket({ ...selectedTicket, archived: false });
    } catch (e) {
      console.error('Failed to restore ticket:', e);
    }
  };

  if (!open || !readyTicket) {
    return null;
  }

  const displayTicket = selectedTicket ?? readyTicket;

  const modalMarkup = (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
        <div
          ref={modalRef}
          className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col min-h-0 min-w-0"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                {editingTicketTitle ? (
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={tempTicketTitle}
                      onChange={(e) => setTempTicketTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveTicketTitle();
                        } else if (e.key === 'Escape') {
                          setEditingTicketTitle(false);
                        }
                      }}
                      className="flex-1 text-xl font-semibold text-gray-900 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />

                    <button type="button" onClick={handleSaveTicketTitle} className="group">
                      <span className="group-hover:[&>*_path]:stroke-custom-green">
                        <Check color={brandColors.darkSilver} size={16} />
                      </span>
                    </button>
                    <button type="button" onClick={() => setEditingTicketTitle(false)} className="group">
                      <span className="group-hover:[&>*_path]:stroke-custom-red">
                        <TrashIcon color={brandColors.darkSilver} size={16} />
                      </span>
                    </button>
                  </div>
                ) : (
                  <h2
                    className="text-xl font-semibold text-gray-900 mb-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors inline-block"
                    onClick={() => {
                      setEditingTicketTitle(true);
                      setTempTicketTitle(displayTicket.title);
                    }}
                    title="Click to edit"
                  >
                    {displayTicket.title}
                  </h2>
                )}
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      displayTicket.priority === 'high'
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : displayTicket.priority === 'medium'
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        : 'bg-green-100 text-green-800 border border-green-200'
                    }`}
                  >
                    {displayTicket.priority}
                  </span>
                  <span className="text-sm text-gray-500">
                    Created on {new Date(displayTicket.createdAt).toLocaleDateString()} at{' '}
                    {new Date(displayTicket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {displayTicket.archived ? (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                      Archived
                    </span>
                  ) : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {displayTicket.archived ? (
                    <Button variant="primary" size="x-small" onClick={handleRestoreTicket}>
                      Restore from archive
                    </Button>
                  ) : (
                    <Button variant="transparent" size="x-small" onClick={handleArchiveTicket}>
                      Archive
                    </Button>
                  )}
                </div>
                {(() => {
                  const linkedSignalId = getSignalIdFromTicketMetadata(displayTicket.metadata);
                  if (!linkedSignalId) return null;
                  return (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-1">Churn signal</p>
                      <Link
                        to={`${AppRoutes.signals}?signalId=${encodeURIComponent(linkedSignalId)}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={() => handleClose()}
                      >
                        View linked signal
                      </Link>
                    </div>
                  );
                })()}
              </div>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                <XIcon size={20} />
              </button>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="flex h-[calc(90vh-140px)] min-h-0 min-w-0">
            {/* Left Panel - Ticket Details */}
            <div className="flex-1 min-w-0 p-6 border-r border-gray-200 overflow-y-auto overflow-x-hidden">
              {/* Description */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-medium text-custom-black">Description</h3>
                </div>
                {editingTicketDescription ? (
                  <div className="space-y-2">
                    <textarea
                      value={tempTicketDescription}
                      onChange={(e) => setTempTicketDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      rows={4}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button variant="primary" size="x-small" onClick={handleSaveTicketDescription}>
                        Save
                      </Button>
                      <Button variant="transparent" size="x-small" onClick={() => setEditingTicketDescription(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="bg-custom-silver rounded-lg p-4 cursor-pointer hover:bg-custom-silver/50 hover:shadow-sm transition-colors"
                    onClick={() => {
                      setEditingTicketDescription(true);
                      setTempTicketDescription(displayTicket.description);
                    }}
                    title="Click to edit"
                  >
                    <p className="text-custom-black text-sm leading-relaxed whitespace-pre-wrap">
                      {displayTicket.description || 'Click to add description...'}
                    </p>
                  </div>
                )}
              </div>

              {/* Related audience members (customers) */}
              <div className="mb-6 min-w-0 max-w-full">
                <h3 className="text-sm font-medium text-custom-black mb-2">Related audience members</h3>
                <p className="text-xs text-custom-darkSilver mb-2">
                  Customers this ticket applies to — you can attach several to one ticket.
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(displayTicket.audienceLinks ?? []).map((link) => {
                    const id = String(link.audienceMemberId);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-custom-silver text-custom-black border border-custom-mediumSilver"
                      >
                        <Link
                          to={generatePath(AppRoutes.memberProfile, { id })}
                          className="truncate max-w-[12rem] text-custom-blue hover:underline"
                          onClick={() => handleClose()}
                        >
                          {audienceLinkLabel(link)}
                        </Link>
                        <button
                          type="button"
                          className="text-custom-darkSilver hover:text-custom-red shrink-0"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void persistTicketAudienceMembers(
                              displayTicket,
                              (displayTicket.audienceLinks ?? [])
                                .map((l) => String(l.audienceMemberId))
                                .filter((x) => x !== id),
                            );
                          }}
                          aria-label="Remove member"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <AudienceMemberListPicker
                  members={availableForDetailTicket}
                  query={memberPickerQuery}
                  onQueryChange={setMemberPickerQuery}
                  isOpen={detailAudiencePickerOpen}
                  onOpen={() => {
                    setAddAudiencePickerOpen(false);
                    setDetailAudiencePickerOpen(true);
                    setMemberPickerQuery('');
                  }}
                  onClose={() => setDetailAudiencePickerOpen(false)}
                  onSelectMember={(id) => {
                    void persistTicketAudienceMembers(displayTicket, [
                      ...(displayTicket.audienceLinks ?? []).map((l) => String(l.audienceMemberId)),
                      id,
                    ]);
                    setMemberPickerQuery('');
                  }}
                  containerRef={detailAudiencePickerRef}
                  collapsedLabel="Click to search and add members…"
                  compact
                />
              </div>

              {/* Due Date */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-custom-black mb-2">Due date</h3>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={dueDateToDateInputValue(displayTicket.dueDate)}
                    onChange={async (e) => {
                      const picked = e.target.value;
                      const newDueDate = picked ? `${picked}T12:00:00.000Z` : undefined;

                      // Update local state immediately
                      setSelectedTicket({
                        ...displayTicket,
                        dueDate: newDueDate,
                      });

                      try {
                        await updateTicketMutation.mutateAsync({
                          ticketId: displayTicket.ticketId,
                          data: picked ? { dueDate: newDueDate } : { clearDueDate: true },
                        });
                      } catch (error) {
                        console.error('Failed to update due date:', error);
                        // Revert on error
                        setSelectedTicket({
                          ...displayTicket,
                          dueDate: displayTicket.dueDate,
                        });
                      }
                    }}
                    className="px-2 py-1 text-xs border border-custom-mediumSilver rounded-lg focus:outline-none"
                  />
                  {displayTicket.dueDate && (
                    <Button
                      variant="transparent"
                      size="x-small"
                      onClick={async () => {
                        // Clear due date
                        setSelectedTicket({
                          ...displayTicket,
                          dueDate: undefined,
                        });

                        try {
                          await updateTicketMutation.mutateAsync({
                            ticketId: displayTicket.ticketId,
                            data: {
                              clearDueDate: true,
                            },
                          });
                        } catch (error) {
                          console.error('Failed to clear due date:', error);
                          // Revert on error
                          setSelectedTicket({
                            ...displayTicket,
                            dueDate: displayTicket.dueDate,
                          });
                        }
                      }}
                      title="Clear due date"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Assignee */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-custom-black mb-2">Assignee</h3>
                <div className="flex items-center gap-3 ">
                  {(() => {
                    const assignee = displayTicket.assigneeId
                      ? findTeamMemberByAssigneeId(teamMembers, displayTicket.assigneeId)
                      : null;
                    return assignee ? (
                      <React.Fragment>
                        <Avatar
                          initials={getDisplayName(assignee)}
                          size="small"
                          imageUrl={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                            getDisplayName(assignee),
                          )}&background=6B7280&color=fff`}
                        />
                        <div className="flex-1">
                          <span className="text-sm text-gray-900">{getDisplayName(assignee)}</span>
                          <p className="text-xs text-gray-500">{assignee.email}</p>
                        </div>
                        <div className="flex items-center gap-2 ">
                          <button type="button" onClick={openAssigneeSelector} className="group">
                            <span className="group-hover:[&>*_path]:stroke-custom-blue">
                              <Pencil color={brandColors.darkSilver} size={16} />
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              updateTicketAssignee(displayTicket.ticketId, undefined);
                            }}
                            className="group"
                          >
                            <span className="group-hover:[&>*_path]:stroke-custom-red">
                              <TrashIcon color={brandColors.darkSilver} size={16} />
                            </span>
                          </button>
                        </div>
                      </React.Fragment>
                    ) : (
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-xs text-custom-darkSilver">Unassigned</span>
                        <Button variant="primary" size="x-small" onClick={openAssigneeSelector}>
                          Assign
                        </Button>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Tags */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-custom-black mb-2">Tags</h3>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Custom Tags */}
                  {displayTicket.tags &&
                    displayTicket.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`px-2 py-1 rounded text-xs font-medium border flex items-center gap-1 ${getTagColor(
                          tag,
                        )}`}
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveLabel(tag)}
                          className="hover:opacity-70 transition-opacity ml-1"
                        >
                          ✕
                        </button>
                      </span>
                    ))}

                  {/* Add Tag Button or Input */}
                  {!showLabelInput ? (
                    <Button variant="transparent" size="x-small" onClick={() => setShowLabelInput(true)}>
                      +
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddLabel();
                          } else if (e.key === 'Escape') {
                            setNewLabel('');
                            setShowLabelInput(false);
                          }
                        }}
                        placeholder="Tag name"
                        className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <Button variant="primary" size="x-small" onClick={handleAddLabel} disabled={isAddingLabel}>
                        {isAddingLabel ? 'Adding...' : 'Add'}
                      </Button>
                      <Button
                        variant="transparent"
                        size="x-small"
                        onClick={() => {
                          setNewLabel('');
                          setShowLabelInput(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Votes */}
              {/* <div className="mb-6">
                  <h3 className="text-sm font-medium text-custom-black mb-2">Votes</h3>
                  <button
                    onClick={() => {
                      const userVote = getUserVote(displayTicket);
                      handleVote(userVote === 'upvote' ? 'remove' : 'upvote');
                    }}
                    className={`px-3 py-2 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 ${
                      getUserVote(displayTicket) === 'upvote'
                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>👍</span>
                    {getUserVote(displayTicket) === 'upvote' ? 'Voted' : 'Vote'}
                    <span className="ml-1 font-semibold">({displayTicket.upvotes?.length || 0})</span>
                  </button>
                </div> */}

              {/* Status */}
              {/* <div className="mb-6">
                  <h3 className="text-sm font-medium text-custom-black mb-2">Status</h3>
                  <div className="flex flex-wrap gap-2">
                    {(['open', 'in_progress', 'resolved', 'closed'] as const).map((status) => {
                      const isActive = displayTicket.status === status;
                      const statusLabels = {
                        open: 'Open',
                        in_progress: 'In Progress',
                        resolved: 'Resolved',
                        closed: 'Closed',
                      };
                      const statusColors = {
                        open: 'bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-300',
                        in_progress: 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300',
                        resolved: 'bg-green-100 text-green-800 hover:bg-green-200 border-green-300',
                        closed: 'bg-red-100 text-red-800 hover:bg-red-200 border-red-300',
                      };

                      return (
                        <button
                          key={status}
                          onClick={async () => {
                            if (isActive) return; // Don't update if already active

                            const previousStatus = displayTicket.status;

                            // Update local state immediately
                            setSelectedTicket({
                              ...displayTicket,
                              status,
                            });

                            try {
                              await updateTicketMutation.mutateAsync({
                                ticketId: displayTicket.ticketId,
                                data: { status },
                              });
                            } catch (error) {
                              console.error('Failed to update status:', error);
                              // Revert on error
                              setSelectedTicket({
                                ...displayTicket,
                                status: previousStatus,
                              });
                            }
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                            isActive
                              ? statusColors[status] + ' ring-2 ring-offset-1 ring-blue-500'
                              : statusColors[status] + ' opacity-60'
                          }`}
                        >
                          {statusLabels[status]}
                        </button>
                      );
                    })}
                  </div>
                </div> */}
            </div>

            {/* Right Panel - Comments and Activity */}
            <div className="flex-1 min-w-0 p-6 overflow-y-auto overflow-x-hidden">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-custom-black">Comments and activity</h3>
              </div>

              {/* Activity log */}
              <div className="mb-6 border-b border-gray-100 pb-4">
                <h4 className="text-xs font-semibold text-custom-darkSilver uppercase tracking-wide mb-2">Activity</h4>
                {activityLoading ? (
                  <p className="text-xs text-gray-500">Loading…</p>
                ) : activityRows.length === 0 ? (
                  <p className="text-xs text-gray-500">No activity yet.</p>
                ) : (
                  <ul className="space-y-2 max-h-40 overflow-y-auto">
                    {activityRows.map((row) => (
                      <li key={row.id} className="text-xs text-custom-black">
                        <span className="text-custom-darkSilver">{new Date(row.createdAt).toLocaleString()}</span>
                        {' · '}
                        <span className="font-medium">{formatTicketActivityActor(row)}</span>
                        {' — '}
                        {row.summary}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Comment Input */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                        user?.attributes?.given_name && user?.attributes?.family_name
                          ? `${user.attributes.given_name} ${user.attributes.family_name}`
                          : user?.attributes?.email || 'User',
                      )}&background=6B7280&color=fff&size=32`}
                      alt="Your avatar"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a comment..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      rows={3}
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        variant="primary"
                        size="x-small"
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || addCommentMutation.isLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center gap-2 min-w-[100px]"
                      >
                        {addCommentMutation.isLoading ? (
                          <>
                            <LoaderCircle className="animate-spin h-4 w-4" />
                            <span>Adding...</span>
                          </>
                        ) : (
                          'Comment'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div className="space-y-4">
                {commentsLoading ? (
                  <div className="text-center py-8">
                    <LoaderCircle className="animate-spin h-6 w-6 mx-auto text-blue-600" />
                    <p className="text-gray-500 text-sm mt-2">Loading comments...</p>
                  </div>
                ) : commentsData && commentsData.length > 0 ? (
                  commentsData
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((comment) => (
                      <div key={comment.commentId} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                          <img
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                              comment.createdByName,
                            )}&background=6B7280&color=fff&size=32`}
                            alt={comment.createdByName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="bg-custom-silver rounded-lg p-3">
                            <p className="text-sm text-custom-black whitespace-pre-wrap">{comment.content}</p>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs font-medium text-custom-black">{comment.createdByName}</span>
                            <span className="text-xs text-custom-darkSilver">
                              {new Date(comment.createdAt).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">No comments yet</p>
                    <p className="text-gray-400 text-xs mt-1">Be the first to comment on this ticket</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showAssigneeSelector &&
        displayTicket &&
        createPortal(
          <div
            ref={assigneeOverlayRef}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            style={{ zIndex: 10000 }}
          >
            <div className="bg-white rounded-lg shadow-xl w-96 max-w-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAssigneeSelector(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ←
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900">Members</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAssigneeSelector(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ✕
                </button>
              </div>
              <div className="p-4 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search members"
                  value={assigneeSearchTerm}
                  onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                  className="w-full min-w-0 px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
              <div className="p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Board members</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredTeamMembers.map((member) => (
                    <div
                      key={assigneeOptionValue(member)}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        updateTicketAssignee(displayTicket.ticketId, assigneeOptionValue(member));
                        setShowAssigneeSelector(false);
                        setAssigneeSearchTerm('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          updateTicketAssignee(displayTicket.ticketId, assigneeOptionValue(member));
                          setShowAssigneeSelector(false);
                          setAssigneeSearchTerm('');
                        }
                      }}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        findTeamMemberByAssigneeId(teamMembers, displayTicket.assigneeId)?.email === member.email
                          ? 'bg-blue-50 border border-blue-200'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                            getDisplayName(member),
                          )}&background=6B7280&color=fff&size=32`}
                          alt={getDisplayName(member)}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-gray-900 text-sm">{getDisplayName(member)}</span>
                    </div>
                  ))}
                  {filteredTeamMembers.length === 0 && (
                    <div className="text-gray-500 text-sm text-center py-4">No members found</div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );

  return createPortal(modalMarkup, document.body);
};

export default TicketDetailModal;
